import { getUser, getToken } from "../utils/authService";
import { useState, useEffect } from "react";
import { FileText, Download, Building, Users, Package, Truck, Wrench, DollarSign, ClipboardList } from "lucide-react";
import { toast } from "sonner";
import { apiClient } from "../utils/apiClient";

const ReportsPage = () => {
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState("");
  const [loading, setLoading] = useState(true);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const response = await apiClient.get('/projects');
      setProjects(response.data);
      if (response.data.length > 0) {
        setSelectedProject(response.data[0].id);
      }
    } catch (error) {
      toast.error("Failed to fetch projects");
    } finally {
      setLoading(false);
    }
  };

  const downloadReport = async (endpoint) => {
    const url = endpoint.includes('{project_id}') 
      ? `${API_URL}/api${endpoint.replace('{project_id}', selectedProject)}`
      : `${API_URL}/api${endpoint}`;
    
    try {
      const token = getToken();
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Download failed');
      }
      
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      
      // Get filename from Content-Disposition header or generate one
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = 'report.pdf';
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?([^"]+)"?/);
        if (match) filename = match[1];
      }
      
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
      
      toast.success("Report downloaded successfully");
    } catch (error) {
      toast.error("Failed to download report");
      console.error("Download error:", error);
    }
  };

  const reportCategories = [
    {
      title: "Project Reports",
      icon: Building,
      color: "bg-primary/10 text-primary",
      requiresProject: true,
      reports: [
        { name: "Project Summary", description: "Overview of project details, BOQ, and progress", endpoint: "/reports/project-summary/{project_id}" },
        { name: "BOQ Report", description: "Bill of Quantities with rates and amounts", endpoint: "/reports/boq/{project_id}" },
        { name: "Work Orders", description: "List of all work orders for project", endpoint: "/reports/work-orders/{project_id}" }
      ]
    },
    {
      title: "Material Reports",
      icon: Package,
      color: "bg-accent/10 text-accent",
      requiresProject: true,
      reports: [
        { name: "Material Stock", description: "Current stock with opening, inward, outward", endpoint: "/reports/material-stock/{project_id}" }
      ]
    },
    {
      title: "Equipment Reports",
      icon: Truck,
      color: "bg-warning/10 text-warning",
      requiresProject: true,
      reports: [
        { name: "Equipment Report", description: "Equipment list with hours and fuel consumption", endpoint: "/reports/equipment/{project_id}" }
      ]
    },
    {
      title: "HR & Payroll",
      icon: Users,
      color: "bg-success/10 text-success",
      requiresProject: false,
      reports: [
        { name: "Employee List", description: "All active employees with details", endpoint: "/reports/employees" },
        { name: "Contractor List", description: "All contractors with contact and bank details", endpoint: "/reports/contractors" }
      ]
    },
    {
      title: "Maintenance",
      icon: Wrench,
      color: "bg-error/10 text-error",
      requiresProject: false,
      reports: [
        { name: "Maintenance Schedule", description: "Upcoming and overdue maintenance", endpoint: "/reports/maintenance" }
      ]
    }
  ];

  if (loading) return <div className="text-center py-12">Loading...</div>;

  return (
    <div>
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="font-heading font-bold text-4xl text-primary tracking-tight">REPORTS</h1>
          <p className="text-slate-600 mt-2 font-body">Download PDF reports for all modules</p>
        </div>
      </div>

      {/* Project Selector */}
      <div className="stat-card mb-8">
        <div className="flex items-center space-x-4">
          <Building className="w-6 h-6 text-primary" />
          <div className="flex-1">
            <label className="block text-xs font-medium text-slate-700 mb-2 uppercase tracking-widest">Select Project for Project-specific Reports</label>
            <select
              value={selectedProject}
              onChange={(e) => setSelectedProject(e.target.value)}
              className="w-full max-w-md px-4 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
            >
              {projects.map(p => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Report Categories */}
      <div className="space-y-8">
        {reportCategories.map((category, idx) => (
          <div key={idx}>
            <div className="flex items-center space-x-3 mb-4">
              <div className={`p-2 rounded-sm ${category.color}`}>
                <category.icon className="w-5 h-5" />
              </div>
              <h2 className="font-heading font-bold text-xl">{category.title}</h2>
              {category.requiresProject && (
                <span className="text-xs text-slate-500 bg-slate-100 px-2 py-1 rounded-sm">Requires Project</span>
              )}
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {category.reports.map((report, ridx) => (
                <div key={ridx} className="stat-card hover:shadow-md transition-shadow">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className="font-heading font-bold text-lg mb-1">{report.name}</h3>
                      <p className="text-sm text-slate-600 mb-4">{report.description}</p>
                    </div>
                    <FileText className="w-8 h-8 text-slate-300" />
                  </div>
                  <button
                    onClick={() => downloadReport(report.endpoint)}
                    disabled={category.requiresProject && !selectedProject}
                    className="btn-outline w-full flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Download className="w-4 h-4" />
                    <span>Download PDF</span>
                  </button>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Payroll Reports */}
      <div className="mt-8">
        <div className="flex items-center space-x-3 mb-4">
          <div className="p-2 rounded-sm bg-primary/10 text-primary">
            <DollarSign className="w-5 h-5" />
          </div>
          <h2 className="font-heading font-bold text-xl">Payroll Reports</h2>
        </div>
        <div className="stat-card">
          <p className="text-sm text-slate-600 mb-4">
            For payroll reports and payslips, please visit the <strong>Payroll Management</strong> page where you can:
          </p>
          <ul className="list-disc list-inside text-sm text-slate-600 space-y-1 mb-4">
            <li>Download monthly payroll summary PDF</li>
            <li>Download individual employee payslips</li>
            <li>View payroll statistics by month</li>
          </ul>
          <a href="/payroll" className="btn-primary inline-flex items-center space-x-2">
            <DollarSign className="w-4 h-4" />
            <span>Go to Payroll</span>
          </a>
        </div>
      </div>

      {/* Tips */}
      <div className="mt-8 p-4 bg-slate-50 rounded-sm border border-slate-200">
        <h3 className="font-heading font-bold text-sm uppercase tracking-widest text-slate-700 mb-2">Tips</h3>
        <ul className="text-sm text-slate-600 space-y-1">
          <li>• All reports are generated in PDF format and will open in a new tab</li>
          <li>• Reports include the KPM Infrastructure header and generation timestamp</li>
          <li>• For large datasets, reports may take a few seconds to generate</li>
          <li>• Project-specific reports require selecting a project first</li>
        </ul>
      </div>
    </div>
  );
};

export default ReportsPage;
